import { computed, type Ref, type WritableComputedRef } from 'vue'

/**
 * `v-model.number` leaves a cleared number input as `''` rather than `null`, which satisfies
 * neither the criterion's `number | null` type nor the API — `window_days: ""` is sent verbatim and
 * rejected, so the preview stops updating. Mapping anything non-numeric back to `null` keeps the
 * bound model honest and lets the completeness checks see an unfinished row for what it is.
 */
export const useNumericField = (
  source: Ref<number | null> | WritableComputedRef<number | null>,
  { min, max }: { min?: number; max?: number } = {},
): WritableComputedRef<number | null> =>
  computed({
    get: () => source.value,
    set: (next) => {
      if (typeof next !== 'number' || !Number.isFinite(next)) {
        source.value = null
        return
      }

      // Clearing the field has to stay possible, so only a real number is held to the bounds.
      const floored = min === undefined ? next : Math.max(next, min)
      source.value = max === undefined ? floored : Math.min(floored, max)
    },
  })
